import React, { useEffect, useState } from "react";
import { clsx } from "clsx";
import { Placeholder } from "@bloomberg/media-ui-article";

import { fetchNewsletterFromApi } from "~api/client/fetchNewsletterFromApi";
import { PreviewErrorCard } from "~components/shared/card/Card";
import { GridModule } from "~components/shared/grid/gridModule";
import Headline from "~components/shared/headline/Headline";
import LazyRetinaImage from "~components/shared/lazy-retina-image/LazyRetinaImage";
import Summary from "~components/shared/summary/Summary";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import { TranslatedMessage } from "~i18n/translate";
import { HEADLINE_SIZE, RequestStatus } from "~types/lineup";
import {
    type Newsletter,
    type VariationComponent,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./NewsletterPhoenix.integrity";
import { NewsletterPhoenixForm } from "./NewsletterPhoenixForm";
import styles from "./NewsletterPhoenix.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [
    ZoneColumnCombo.leftContentWell,
    ZoneColumnCombo.gridRightRail,
    ZoneColumnCombo.fullWidthContentWell,
];

enum NewsletterImagePlacement {
    Default = "card.default",
    Mobile = "card.mobile",
    Standalone = "card.standalone",
}

const SUBSCRIBER_ONLY = "subscriberOnly";

const getNewsletterImage = (
    newsletter?: Newsletter,
    placement = NewsletterImagePlacement.Mobile
) => {
    return newsletter?.images?.find((img) =>
        img?.placement?.includes(placement)
    );
};

export default function NewsletterPhoenix(props: VariationComponent) {
    const { id, renderingLocation, embedUrl = "" } = props;

    const [newsletter, setNewsletter] = useState<Newsletter>();
    const [requestStatus, setRequestStatus] = useState<RequestStatus>(
        RequestStatus.Pending
    );

    const fetchNewsletter = () => {
        setRequestStatus(RequestStatus.Pending);
        fetchNewsletterFromApi({ slug: embedUrl })
            .then((_newsletter) => {
                if (_newsletter) {
                    setNewsletter(_newsletter);
                }
                setRequestStatus(RequestStatus.Done);
            })
            .catch(() => {
                setRequestStatus(RequestStatus.Error);
            });
    };

    useEffect(() => {
        if (embedUrl.length > 1) fetchNewsletter();

        return;
    }, []);

    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    const image = props.image ?? getNewsletterImage(newsletter);
    const title = props.title ?? newsletter?.title;
    const summary = props.subtitle ?? newsletter?.description;
    const listId = props.listId ?? newsletter?.signUpList;
    const sampleUrl = newsletter?.sampleUrl;
    const isSubscriberOnly = newsletter?.subscriptionType === SUBSCRIBER_ONLY;

    if (requestStatus === RequestStatus.Error) {
        return (
            <PreviewErrorCard
                className={styles.errorCard}
                message="Please confirm the Newsletter Slug is properly formatted, there was an error retrieving newsletter data."
            />
        );
    }

    return requestStatus === RequestStatus.Done ? (
        <VariantBase
            source={props}
            className={clsx(styles.NewsletterPhoenix, {
                [styles.rail]: renderingLocation === "gridRightRail",
            })}
            supportedRegions={SUPPORTED_REGIONS}
        >
            <LazyRetinaImage
                image={image}
                desktopMaxWidth={141}
                mobileMaxWidth={96}
                className={styles.image}
                aspectRatio={1}
            />
            <div className={styles.container}>
                <div className={styles.contentContainer}>
                    <LazyRetinaImage
                        image={image}
                        desktopMaxWidth={141}
                        mobileMaxWidth={96}
                        className={styles.image2}
                        aspectRatio={1}
                    />
                    <div>
                        {isSubscriberOnly && (
                            <p className={styles.subscriberOnlyLabel}>
                                <TranslatedMessage
                                    id="subscriberOnly"
                                    defaultMessage="Subscriber Only"
                                />
                            </p>
                        )}
                        {title ? (
                            <Headline
                                text={title}
                                isPhoenix
                                size={HEADLINE_SIZE.EXTRA_SMALL}
                                className={styles.headline}
                            />
                        ) : null}
                        <Summary summary={summary} isPhoenix />
                        {sampleUrl ? (
                            <a
                                href={newsletter?.sampleUrl}
                                className={styles.cta}
                            >
                                <TranslatedMessage
                                    id="preview"
                                    defaultMessage="Preview"
                                />
                            </a>
                        ) : null}
                    </div>
                </div>
                <NewsletterPhoenixForm
                    isSubscriberOnly={isSubscriberOnly}
                    listId={listId}
                    id={id}
                />
            </div>
        </VariantBase>
    ) : (
        <GridModule>
            <Placeholder>
                <div>
                    <Placeholder.Line />
                    <Placeholder.Line />
                    <Placeholder.Line />
                    <Placeholder.Line />
                    <Placeholder.Line />
                    <Placeholder.Line />
                    <Placeholder.Line />
                    <Placeholder.Line />
                </div>
            </Placeholder>
        </GridModule>
    );
}
